import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";

import Navigation from "./components/site/Navigation";
import Footer from "./components/site/Footer";
import FloatingCTA from "./components/site/FloatingCTA";
import HomePage from "./pages/HomePage";
import BuildersPage from "./pages/BuildersPage";
import WhyPage from "./pages/WhyPage";
import { FAQS } from "./lib/config";

// Reset scroll on route change. If the target route has a hash, smooth-scroll to it.
const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait for the target section to mount, then scroll.
      const id = hash.replace("#", "");
      const attempt = (retries = 6) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (retries > 0) {
          setTimeout(() => attempt(retries - 1), 100);
        }
      };
      // Small delay lets the new route mount first.
      setTimeout(() => attempt(), 60);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pathname, hash]);
  return null;
};

// Manage document.title, meta description, canonical, and og:url per route.
const RouteMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const url = origin + pathname;

    let title;
    let description;
    if (pathname.startsWith("/builders")) {
      title =
        "For Builders | Ibex Acquisitions & Development — Your Off-Market Land Pipeline";
      description =
        "Ibex partners with a limited number of builders per market — first-look, off-market lot sourcing built around your buy box. Fewer partners, deeper relationships, better dirt.";
    } else if (pathname.startsWith("/why")) {
      title =
        "Why Ibex | Facing Foreclosure or a Property You Can’t Carry? | Ibex Acquisitions & Development";
      description =
        "Foreclosures are up 21% nationwide and 57% in Colorado this year. Ibex explains what a foreclosure does to your credit, what your real options are, and how a fast, fair cash sale can keep it off your record.";
    } else {
      title =
        "Sell Your House or Land Fast for Cash | Ibex Acquisitions & Development | Denver, CO & Nationwide";
      description =
        "Ibex buys houses in any condition and land of every kind — nationwide, with deep roots in Denver, Colorado. No fees, no repairs, no commissions. Cash offer within 48 hours.";
    }
    document.title = title;

    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:type"]', "content", "website");

    // og:url + canonical
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement("meta");
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute("content", url);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
  }, [pathname]);
  return null;
};

// Inject Organization structured data once at app root (site-wide).
const OrganizationStructuredData = () => {
  useEffect(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-ibex-jsonld", "org");
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: "Ibex Acquisitions & Development",
      alternateName: "Ibex",
      description:
        "Family-owned buyers of houses and land. Fair, no-obligation cash offers on property nationwide, with deep, on-the-ground expertise in Denver and the greater Colorado market. Off-market land pipeline for builders.",
      url: origin || "https://ibexacquisitions.com",
      logo: (origin || "") + "/ibex-logo.png",
      image: (origin || "") + "/ibex-logo.png",
      email: "Brandon@Ibexacquisitions.com",
      parentOrganization: {
        "@type": "Organization",
        name: "Vaulti LLC",
      },
      founder: [
        { "@type": "Person", name: "Brandon" },
        { "@type": "Person", name: "Katana" },
      ],
      areaServed: [
        { "@type": "Country", name: "United States" },
        { "@type": "State", name: "Colorado" },
        { "@type": "City", name: "Denver" },
      ],
      slogan: "Invest. Build. Elevate. Expand.",
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);
  return null;
};

// Inject FAQPage JSON-LD only on the homepage.
const FAQStructuredData = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname !== "/") return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-ibex-jsonld", "faq");
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [pathname]);
  return null;
};

function AppShell() {
  return (
    <div className="min-h-screen" style={{ background: "#FAF7F2" }} data-testid="app-root">
      <ScrollManager />
      <RouteMeta />
      <OrganizationStructuredData />
      <FAQStructuredData />
      <Navigation />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/why" element={<WhyPage />} />
        <Route path="/builders" element={<BuildersPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
      <Footer />
      <FloatingCTA />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
